import { FC, useEffect, useState } from 'react';
import { WordRow } from 'src/apps/Wordlists/WordRow';
import { WordlistWord } from 'src/apps/Wordlists/WordlistDetailsPage';
import { Creator } from 'src/api/wordlists/types';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Toast } from 'src/components';
import { SelectionState, SelectionActions } from 'src/types/selection';
import * as S from './styles';

const msgs = defineMessages({
  markedAsDifficult: { id: 'words_detail_marked_difficult' },
  markedAsKnown: { id: 'words_detail_marked_known' },
  unmarkedAsKnown: { id: 'wordlist_details_unmarked_as_known_toast' },
  unmarkedAsDifficult: { defaultMessage: 'Unmarked as difficult' }, // TO-DO: add new ID once CCCRL-2244 is done
});

export type ToastTypes = 'asKnown' | 'asDifficult' | 'unmarkedAsKnown' | 'unmarkedAsDifficult';

const TOAST_DURATION = 5000;

interface Props {
  items: WordlistWord[];
  showOptions?: boolean;
  isSingleColumn?: boolean;
  wordlistCreator?: Creator;
  hideDifficultToggle?: boolean;
  hideMarkAsKnownToggle?: boolean;
  hideWordInfoToggle?: boolean;
  refetchLearnables?: () => void;
  onWordMarkedAsKnown?: (wordId: number) => void;
  sectionType?: 'learned' | 'toLearn';
  selectionState?: SelectionState;
  selectionActions?: SelectionActions;
  wordlistId?: string;
}

export const WordRowList: FC<Props> = ({
  items,
  showOptions,
  isSingleColumn,
  wordlistCreator,
  hideDifficultToggle,
  hideMarkAsKnownToggle,
  hideWordInfoToggle,
  refetchLearnables,
  onWordMarkedAsKnown,
  sectionType,
  selectionState,
  selectionActions,
  wordlistId,
}) => {
  const [isToastVisible, setIsToastVisible] = useState<boolean>(false);
  const [toastStatus, setToastStatus] = useState<'success' | 'info'>('success');
  const [toastMessage, setToastMessage] = useState<MessageDescriptor>({});
  const intl = useIntl();

  useEffect(() => {
    if (isToastVisible) {
      const timeoutId = setTimeout(() => {
        setIsToastVisible(false);
      }, TOAST_DURATION);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isToastVisible]);

  /* istanbul ignore next: logic covered by one test case*/
  const handleShowToast = (type: ToastTypes) => {
    switch (type) {
      case 'asKnown':
        setToastStatus('success');
        setToastMessage(msgs.markedAsKnown);
        break;
      case 'asDifficult':
        setToastStatus('success');
        setToastMessage(msgs.markedAsDifficult);
        break;
      case 'unmarkedAsKnown':
        setToastStatus('info');
        setToastMessage(msgs.unmarkedAsKnown);
        break;
      case 'unmarkedAsDifficult':
        setToastStatus('info');
        setToastMessage(msgs.unmarkedAsDifficult);
        break;
    }

    setIsToastVisible(true);
  };

  return (
    <S.Container data-testid="wordrow-list">
      {items.map(item => (
        <WordRow
          key={`word-row-${item.id}`}
          word={item}
          options={showOptions}
          isSingleColumn={isSingleColumn}
          wordlistCreator={wordlistCreator}
          hideDifficultToggle={hideDifficultToggle}
          hideMarkAsKnownToggle={hideMarkAsKnownToggle}
          hideWordInfoToggle={hideWordInfoToggle}
          refetchLearnables={refetchLearnables}
          onWordMarkedAsKnown={onWordMarkedAsKnown}
          difficult={item.difficult}
          known={item.known || item.isLearned}
          handleShowToast={handleShowToast}
          sectionType={sectionType}
          selectionState={selectionState}
          selectionActions={selectionActions}
          allWordIds={items.map(word => word.id)}
          wordlistId={wordlistId}
        />
      ))}
      {isToastVisible && (
        <S.ToastWrapper>
          <Toast
            status={toastStatus}
            snackBar
            hideClose
            shouldCloseOnEsc
            shouldCloseOnOutsideClick
            onClick={() => setIsToastVisible(false)}
          >
            {intl.formatMessage(toastMessage)}
          </Toast>
        </S.ToastWrapper>
      )}
    </S.Container>
  );
};
